'use client';

import React from 'react';
import { type ReactNode } from 'react';
import { Field } from '../field';
import type { InputBaseProps } from './Input';
import { TextField } from './Input';
import { cx, sortCx } from '../../../utils/cx';

const SEMANTIC_NAME = 'RomaInputGroup';

// `${string}ClassName` is used to omit any className prop that ends with a `ClassName` suffix
export interface InputGroupProps
    extends Omit<InputBaseProps, 'type' | 'icon' | 'placeholder' | 'tooltip' | `${string}ClassName`> {
    /** A prefix text that is displayed in the same box as the input.*/
    prefix?: string;
    /** A leading addon that is displayed with visual separation from the input. */
    leadingAddon?: ReactNode;
    /** A trailing addon that is displayed with visual separation from the input. */
    trailingAddon?: ReactNode;
    /** The class name to apply to the input group. */
    className?: string;
    /** The children of the input group (i.e `<Input.Base />`) */
    children: ReactNode;
}

export const InputGroup = ({
    size = 'sm',
    prefix,
    leadingAddon,
    trailingAddon,
    label,
    hint,
    children,
    ...props
}: InputGroupProps) => {
    const hasLeading = !!leadingAddon;
    const hasTrailing = !!trailingAddon;

    const styles = sortCx({
        sm: {
            root: 'rounded-xs',
            input: cx(
                // Apply padding styles when select element is passed as a child
                hasLeading && 'group-has-[&>select]:px-2.5 group-has-[&>select]:pl-2.5',
                hasTrailing &&
                    (prefix
                        ? 'group-has-[&>select]:pr-6 group-has-[&>select]:pl-0'
                        : 'group-has-[&>select]:pr-6 group-has-[&>select]:pl-3'),
            ),
            leadingText: 'pl-3',
        },
        md: {
            root: 'rounded-xs',
            input: cx(
                // Apply padding styles when select element is passed as a child
                hasLeading && 'group-has-[&>select]:px-3 group-has-[&>select]:pl-3',
                hasTrailing &&
                    (prefix
                        ? 'group-has-[&>select]:pr-6 group-has-[&>select]:pl-0'
                        : 'group-has-[&>select]:pr-6 group-has-[&>select]:pl-3'),
            ),
            leadingText: 'pl-3.5',
        },
        lg: {
            root: 'rounded-sm',
            input: cx(
                // Apply padding styles when select element is passed as a child
                hasLeading && 'group-has-[&>select]:px-3.5 group-has-[&>select]:pl-3.5',
                hasTrailing &&
                    (prefix
                        ? 'group-has-[&>select]:pr-6 group-has-[&>select]:pl-0'
                        : 'group-has-[&>select]:pr-6 group-has-[&>select]:pl-3.5'),
            ),
            leadingText: 'pl-3.5',
        },
    });

    return (
        <Field
            as={TextField}
            size={size}
            aria-label={label || undefined}
            inputClassName={cx(
                styles[size].input,
                // Apply styles based on the presence of leading or trailing elements
                hasLeading && 'rounded-l-none',
                hasTrailing && 'rounded-r-none',
            )}
            tooltipClassName={cx(hasTrailing && !hasLeading && 'group-has-[&>select]:right-0')}
            wrapperClassName={cx(
                'z-10',
                // Apply styles based on the presence of leading or trailing elements
                hasLeading && 'rounded-l-none',
                hasTrailing && 'rounded-r-none',
                // When select element is passed as a child
                'group-has-[&>select]:bg-transparent group-has-[&>select]:ring-0 group-has-[&>select]:focus-within:ring-0',
                // In `Input` component, there is "group-disabled" class so here we need to use "group-disabled:group-has-[&>select]" to avoid conflict
                'group-disabled:group-has-[&>select]:bg-transparent',
            )}
            {...props}
        >
            {({ isDisabled, isInvalid, isRequired }) => (
                <>
                    {label && (
                        <Field.Label size={size} isRequired={isRequired}>
                            {label}
                        </Field.Label>
                    )}

                    <div
                        data-input-size={size}
                        className={cx(
                            'group relative flex h-max w-full flex-row justify-center bg-primary transition-all duration-100 ease-linear',
                            styles[size].root,

                            // Only apply focus ring when child is select and input is focused
                            'has-[&>select]:ring-1 has-[&>select]:ring-primary has-[&>select]:ring-inset has-[&>select]:has-[input:focus]:ring-2 has-[&>select]:has-[input:focus]:ring-primary_focused',

                            isDisabled &&
                                'cursor-default has-[&>select]:bg-disabled_subtle has-[&>select]:ring-disabled',
                            isInvalid && 'has-[&>select]:ring-error_subtle has-[&>select]:has-[input:focus]:ring-error',
                        )}
                    >
                        {leadingAddon && <section data-leading={hasLeading || undefined}>{leadingAddon}</section>}

                        {prefix && (
                            <span className={cx('my-auto grow pr-2', styles[size].leadingText)}>
                                <p className={cx('text-md text-tertiary', isDisabled && 'text-disabled')}>{prefix}</p>
                            </span>
                        )}

                        {children}

                        {trailingAddon && <section data-trailing={hasTrailing || undefined}>{trailingAddon}</section>}
                    </div>

                    {hint && <Field.HintText isInvalid={isInvalid}>{hint}</Field.HintText>}
                </>
            )}
        </Field>
    );
};

InputGroup.displayName = SEMANTIC_NAME;
